import { useCallback, useEffect, useMemo, useState } from "react";
import type { PharmacyMarker } from "./pharmacy-map.types";
import { getCachedRadius, setCachedRadius } from "./pharmacy-map-cache";
import {
	buildRadiusOptions,
	filterPharmaciesByRadius,
	parsePersistedRadius,
	type RadiusOptionValue,
} from "./radius-options";
import {
	applyLegacyMapRadiusBound,
	resolveEffectiveRadius,
	resolveExternalVendorsFiltered,
} from "./vendor-filter-map-wrapper.utils";

type UseMapRadiusVendorsParams<TItem> = {
	showRadiusControl: boolean;
	enablePharmacyMap: boolean;
	allVendors: PharmacyMarker[];
	radiusScopedVendors: PharmacyMarker[];
	filteredVendors: readonly TItem[];
	filteredAggregatedCount: { top: number; all: number } | null | undefined;
	isVendorsLoading: boolean;
};

/**
 * Radius state (selection, session persistence, effective value) plus the
 * marker set and the single-fetch page handed to the picker list. Extracted
 * from VendorFilterMapWrapper to keep that component under the cognitive
 * complexity limit; behaviour is unchanged.
 */
export function useMapRadiusVendors<TItem extends { id: string | number }>({
	showRadiusControl,
	enablePharmacyMap,
	allVendors,
	radiusScopedVendors,
	filteredVendors,
	filteredAggregatedCount,
	isVendorsLoading,
}: UseMapRadiusVendorsParams<TItem>) {
	const radiusOptions = useMemo(
		() => buildRadiusOptions(radiusScopedVendors),
		[radiusScopedVendors],
	);
	const [selectedRadius, setSelectedRadiusState] =
		useState<RadiusOptionValue | null>(null);
	const [sessionRadius, setSessionRadius] = useState<RadiusOptionValue | null>(
		null,
	);
	const setSelectedRadius = useCallback((value: RadiusOptionValue) => {
		setSelectedRadiusState(value);
		setCachedRadius(String(value));
	}, []);
	// Read sessionStorage after mount (SSR has no sessionStorage). Do it
	// once here — not after vendors land — so restoring cannot setState
	// while the picker map is already open.
	useEffect(() => {
		setSessionRadius(parsePersistedRadius(getCachedRadius()));
	}, []);
	const radius = resolveEffectiveRadius({
		selectedRadius,
		isVendorsLoading,
		radiusOptions,
		sessionRadius,
	});
	// The selected radius is what the map shows: bucketed client-side from the
	// single unbounded response instead of one request per option. Without the
	// control there is no selection to apply, so the markers fall back to the
	// pre-MKP-2054 bound instead of the whole unbounded page.
	const vendors = useMemo(
		() =>
			showRadiusControl
				? filterPharmaciesByRadius(allVendors, radius)
				: applyLegacyMapRadiusBound(allVendors),
		[allVendors, radius, showRadiusControl],
	);
	const externalVendorsFiltered = useMemo(
		() =>
			resolveExternalVendorsFiltered({
				enablePharmacyMap,
				showRadiusControl,
				filteredVendors,
				visibleMarkers: vendors,
				aggregatedCount: filteredAggregatedCount,
				isLoading: isVendorsLoading,
			}),
		[
			enablePharmacyMap,
			showRadiusControl,
			filteredVendors,
			vendors,
			filteredAggregatedCount,
			isVendorsLoading,
		],
	);
	return {
		radius,
		radiusOptions,
		selectedRadius,
		setSelectedRadius,
		vendors,
		externalVendorsFiltered,
	};
}
